import React, { useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { Analytics as VercelAnalytics } from '@vercel/analytics/react';
import { BrowserRouter, Routes, Route, Navigate, useParams, useNavigate, useLocation } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';
import Layout from './components/Layout';
import Logo from './components/common/Logo';

// --- PAGE IMPORTS (Kategorize Edilmiş) ---

// site
import LandingPage from './pages/site/LandingPage';
import Pricing from './pages/site/Pricing';
import Legal from './pages/site/Legal';
import AboutUs from './pages/site/AboutUs';
import Contact from './pages/site/Contact';
import WhyUgra from './pages/site/WhyUgra';
import References from './pages/site/References';

// auth
import Login from './pages/auth/Login';
import SuperAdminLogin from './pages/auth/SuperAdminLogin';
import SetupWizard from './pages/auth/SetupWizard';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import AcceptInvite from './pages/auth/AcceptInvite';

// dashboard
import Dashboard from './pages/dashboard/Dashboard';
import Customers from './pages/dashboard/Customers';
import Settings from './pages/dashboard/settings/index';
import Promotions from './pages/dashboard/promotions/index';
import MenuManagement from './pages/dashboard/menuManagement/index';
import Calendar from './pages/dashboard/Calendar';
import Accounting from './pages/dashboard/Accounting';
import Inventory from './pages/dashboard/Inventory';
import Tasks from './pages/dashboard/Tasks';
import Notifications from './pages/dashboard/Notifications';
import Orders from './pages/dashboard/Orders';
import Integrations from './pages/dashboard/Integrations';
import Analytics from './pages/dashboard/Analytics';
import Support from './pages/dashboard/Support';

// admin
import AdminPanel from './pages/admin/AdminPanel';
import Billing from './pages/admin/Billing';
import Activities from './pages/admin/Activities';
import UsersAndRoles from './pages/admin/UsersAndRoles';

// portal
import CustomerPortal from './pages/portal/CustomerPortal';
import GlobalCustomerPortal from './pages/portal/GlobalCustomerPortal';
import CheckIn from './pages/portal/CheckIn';
import CustomerLogin from './pages/portal/CustomerLogin';

// shortlink (leadpin entegrasyonu)
import ShortLink from './pages/ShortLink';

// --- CONFIG & CONTEXT ---
import { supabase } from './supabase/config';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CustomerProvider } from './context/CustomerContext';
import { ThemeProvider } from './context/ThemeContext';
import { AlertProvider } from './context/AlertContext';
import Checkout from './pages/Checkout';
import DocsApp from './pages/docs/DocsApp';
import ScrollToTop from './components/ScrollToTop';
import { initOneSignal } from './utils/pushNotifications';
import './index.css';

const ErrorFallback = ({ error }) => {
  useEffect(() => {
    Sentry.captureException(error);
  }, [error]);

  return (
    <div style={{ padding: '2rem', textAlign: 'center', background: '#fef2f2', minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
      <h1 style={{ color: '#dc2626', marginBottom: '1rem' }}>Sistem Hatası olustu</h1>
      <pre style={{ background: '#eee', padding: '1rem', borderRadius: '8px', maxWidth: '100%', overflow: 'auto' }}>{error.message}</pre>
      <button onClick={() => window.location.reload()} style={{ marginTop: '1rem', background: 'var(--primary)', color: 'var(--text-on-primary)', padding: '10px 20px', borderRadius: '8px', border: 'none', cursor: 'pointer' }}>Sayfayi Yenile</button>
    </div>
  );
};

const PortalSessionGate = () => {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const [hasSession, setHasSession] = useState(null);

  useEffect(() => {
    const checkSession = async () => {
      try {
        if (!tenantId) { navigate('/portal', { replace: true }); return; }

        const globalId = localStorage.getItem('crm_global_customer_id');
        if (!globalId) {
          navigate(`/check-in/${tenantId}`, { replace: true });
          return;
        }

        // Get global user's email
        const { data: globalRec } = await supabase
            .from('customers')
            .select('email')
            .eq('id', globalId)
            .maybeSingle();

        if (!globalRec?.email) {
          navigate(`/check-in/${tenantId}`, { replace: true });
          return;
        }

        // Resolve tenant ID (could be slug)
        let actualTenantId = null;
        const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(tenantId);

        if (isUUID) {
          const { data: idData } = await supabase.from('tenants').select('id').eq('id', tenantId).maybeSingle();
          if (idData) actualTenantId = idData.id;
        }

        if (!actualTenantId) {
          const { data: slugData } = await supabase.from('tenants').select('id').eq('slug', tenantId).maybeSingle();
          if (slugData) actualTenantId = slugData.id;
        }

        if (!actualTenantId) {
          navigate(`/check-in/${tenantId}`, { replace: true });
          return;
        }

        // Check if user has a customer record for this tenant
        const { data: localMatch } = await supabase
            .from('customers')
            .select('id')
            .eq('tenant_id', actualTenantId)
            .eq('email', globalRec.email)
            .maybeSingle();

        if (localMatch) {
          setHasSession(true);
        } else {
          navigate(`/check-in/${tenantId}`, { replace: true });
        }
      } catch (err) {
        console.error("PortalSessionGate error:", err);
        navigate(`/check-in/${tenantId}`, { replace: true });
      }
    };
    checkSession();
  }, [tenantId, navigate]);

  if (hasSession === null) return (
    <div style={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-main)' }}>
      <p style={{ color: 'var(--text-muted)' }}>Yukleniyor...</p>
    </div>
  );
  return <CustomerPortal />;
};

const IndexRoute = () => {
  const hostname = window.location.hostname;
  const { user } = useAuth();

  if (hostname === 'panel.ugra.io') {
    return user ? <Navigate to="/app" /> : <Navigate to="/login" />;
  }

  if (hostname === 'app.ugra.io') {
    return <GlobalCustomerPortal />;
  }

  return <LandingPage />;
};

const AppContent = () => {
  const { user, userData, tenantData, loading, effectiveTenantId } = useAuth();

  console.log('[AppContent] loading:', loading, 'user:', !!user, 'userData:', !!userData, 'tenantData:', !!tenantData);

  if (loading) return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-main)', color: 'var(--text-main)', gap: '1.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', animation: 'pulse 2s infinite' }}>
        <Logo size={48} />
        <span style={{ fontSize: '2.5rem', fontWeight: 600, fontFamily: 'var(--font-logo)', position: 'relative', top: '3px' }}>ugra</span>
      </div>
      <div className="loading-spinner" style={{ width: '32px', height: '32px', border: '3px solid var(--border)', borderTopColor: '#6f4e37', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } } @keyframes pulse { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.8; transform: scale(0.95); } }`}</style>
    </div>
  );

  const hostname = window.location.hostname;
  if (hostname === 'docs.ugra.io') {
    return (
      <ThemeProvider>
        <DocsApp />
      </ThemeProvider>
    );
  }
  
  return (
    <ThemeProvider>
      <Routes>
        <Route path="/" element={<IndexRoute />} />
        <Route path="/login" element={!user ? <Login /> : <Navigate to="/app" />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/signup" element={!user ? <SetupWizard /> : <Navigate to="/app" />} />
        <Route path="/accept-invite" element={<AcceptInvite />} />
        <Route path="/check-in/:tenantId" element={<CheckIn />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/why-ugra" element={<WhyUgra />} />
        <Route path="/referanslarimiz" element={<References />} />
        <Route path="/about" element={<AboutUs />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/legal" element={<Legal />} />
        <Route path="/integrations" element={<Integrations />} />
        <Route path="/checkout" element={user ? <Checkout /> : <Navigate to="/login" />} />
        <Route path="/kvkk" element={<Legal defaultSection="kvkk" />} />
        <Route path="/privacy" element={<Legal defaultSection="privacy" />} />
        <Route path="/sales-agreement" element={<Legal defaultSection="sales" />} />
        <Route path="/customer-login" element={<CustomerLogin />} />
        <Route path="/super-admin-login" element={<SuperAdminLogin />} />
        <Route path="/portal" element={<GlobalCustomerPortal />} />
        <Route path="/portal/:tenantId" element={<PortalSessionGate />} />
        <Route path="/portal/:tenantId/:customerId" element={<CustomerPortal />} />
        <Route path="/super-admin-panel" element={userData?.isSuperAdmin ? <CustomerProvider><Layout /></CustomerProvider> : <Navigate to="/super-admin-login" />}>
          <Route index element={<AdminPanel />} />
        </Route>

        <Route path="/app" element={user ? <CustomerProvider><Layout /></CustomerProvider> : <Navigate to="/login" />}>
          <Route index element={userData?.isSuperAdmin && !effectiveTenantId ? <Navigate to="/app/admin" /> : <Dashboard />} />
          <Route path="customers" element={<Customers />} />
          <Route path="customer-logs" element={<Customers />} />
          <Route path="analytics" element={<Analytics />} />
          <Route path="campaigns" element={<Promotions />} />
          <Route path="campaigns/rewards" element={<Promotions />} />
          <Route path="campaigns/coupons" element={tenantData?.plan === 'pro' || tenantData?.plan === 'premium' ? <Promotions /> : <Navigate to="/app" />} />
          <Route path="activities" element={<Activities />} />
          <Route path="notifications" element={<Notifications />} />
          <Route path="notifications/*" element={<Notifications />} />
          <Route path="users-roles" element={<UsersAndRoles />} />
          <Route path="settings" element={<Settings />} />
          <Route path="settings/*" element={<Settings />} />
          <Route path="support" element={<Support />} />
          <Route path="menu" element={<MenuManagement />} />
          <Route path="menu/*" element={<MenuManagement />} />
          <Route path="accounting" element={tenantData?.plan === 'pro' || tenantData?.plan === 'premium' ? <Accounting /> : <Navigate to="/app" />} />
          <Route path="inventory" element={tenantData?.plan === 'pro' || tenantData?.plan === 'premium' ? <Inventory /> : <Navigate to="/app" />} />
          <Route path="orders" element={tenantData?.plan === 'pro' || tenantData?.plan === 'premium' ? <Orders /> : <Navigate to="/app" />} />
          {userData?.isSuperAdmin && <><Route path="admin" element={<AdminPanel />} /><Route path="billing" element={<Billing />} /><Route path="calendar" element={<Calendar />} /><Route path="tasks" element={<Tasks />} /></>}
        </Route>

        {/* LeadPin short link - ugra.io/abcd tıklama takibi */}
        <Route path="/:shortId" element={<ShortLink />} />

        <Route path="*" element={<Navigate to={user ? "/app" : "/"} replace />} />
      </Routes>
    </ThemeProvider>
  );
};

function App() {
  useEffect(() => {
    initOneSignal();
  }, []);

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <AlertProvider>
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <AppContent />
        </BrowserRouter>
      </AuthProvider>
      </AlertProvider>
      <VercelAnalytics />
    </ErrorBoundary>
  );
}

export default App;
